import AtCustResponseDTO from "@/api/dto/response/AtCustResponseDTO";
import IqCustResponseDTO from "@/api/dto/response/IqCustResponseDTO";
import { AutopayUserInfo, SearchCustInfoState, UserInfo } from "@/types/User";
import { captureException } from '@sentry/nextjs';

export function addParamToLink(url: string, param: object) {
  const hasNoDomain = !url.includes('http');
  if (hasNoDomain) {
    url = `https://tmp.com${url}`;
  }

  const newURL = new URL(url);
  Object.entries(param).forEach(([key, val]) => {
    if (Array.isArray(val)) {
      val.forEach((v) => {
        newURL.searchParams.append(key, v);
      });
    } else {
      newURL.searchParams.append(key, val);
    }
  });

  if (hasNoDomain) {
    return newURL.toString().replace('https://tmp.com', '');
  } else {
    return newURL.toString();
  }
}

export function formatPhoneNumber(phoneNumber: string) {
  const exceptNumberRegex = /[^\d]/g;
  const phoneNumRegex = /(^02|^0505|^1\d{3}|^0\d{2})(\d+)?(\d{4})$/;

  return phoneNumber
    .replace(exceptNumberRegex, '')
    .replace(phoneNumRegex, '$1-$2-$3')
    .replace('--', '-');
}

export function isString(value: unknown): value is string {
  return typeof value === 'string';
}

export function cleanMessage(errorMessage: string) {
  if(errorMessage.includes('AxiosError:')) {
    errorMessage = errorMessage.replace('AxiosError:', '').trim();
  } else if(errorMessage.includes('ORA-06502')) {
    errorMessage = '해지 사유의 경우 256바이트 크기제한이 있습니다.';
  }
  return errorMessage;
}

export function queryToString(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] : value ?? '';
}

// 고객명 마스킹처리하기
export function nameMasking(name: string) {
  let result = "";
  const strLength = name.length;

  if (strLength < 7) {
      result += name.substring(0,1);
      if(strLength == 2) {
          result += "*";
      } else {
          let mask = "";
          for(let i=0; i<(strLength-2); i++) {
              mask += "*";
          }
          result += mask;
          result += name.substring((strLength-1), strLength);
      }
  } else {
      result += name.substring(0,2);
      let mask = "";
      for(let i=0; i<(strLength-4); i++) {
          mask += "*";
      }
      result += mask;
      result += name.substring((strLength-2), strLength);
  }
  return result;
}


export function isInputValid(state: SearchCustInfoState) {
  switch (state.searchOption) {
    case 'phoneNumber':
      return state.phoneNumber.length === 13 && state.userName.length > 0;
    case 'userNumKey':
      return state.userNumKey.length > 0 && state.userName.length > 0;
  }
}


// 유저정보 snake케이스 -> 카멜케이스
export function snakeCaseToCamelCase(customer : AtCustResponseDTO['data']['results'][0] | IqCustResponseDTO['data']['results'][0]) {
  if ('recurrentId' in customer || 'recurrent_id' in customer) {
    const autopayCustomer = customer as any; // autopay 필드에 접근하기 위해 any 캐스팅
    return {
      userName: customer.user_name,
      userNumKey: customer.user_num_key,
      addr: customer.addr,
      recurrentId: 'recurrentId' in customer ? customer.recurrentId : autopayCustomer.recurrent_id,
      payMethod: customer.pay_mthd,
      autopayScheduledInfo: autopayCustomer.autopay_scheduled_info,
      autopayBeginYm: autopayCustomer.autopay_begin_ym,
    } as AutopayUserInfo;
  } else {
    return {
      userName: customer.user_name,
      userNumKey: customer.user_num_key,
      addr: customer.addr,
      payMethod: customer.pay_mthd
    } as UserInfo;
  }
}

export function convertUserListToCamelCase(
  usIf: (AtCustResponseDTO['data']['results'][0] | IqCustResponseDTO['data']['results'][0])[],
): UserInfo[] | AutopayUserInfo[] {
  return usIf.map((customer) => snakeCaseToCamelCase(customer));
}

export function validateCitygasCode(
  value: string | string[] | undefined,
  context: any
): string | null {
  const cgcode = queryToString(value);
  if (
    Array.isArray(value) ||
    !cgcode ||
    typeof cgcode !== 'string' ||
    cgcode === '[citygasCode]' ||
    cgcode.includes('[') ||
    cgcode.includes(']')
  ) {
    captureException(new Error('Invalid citygasCode'), {
      tags: { citygasCode: Array.isArray(value) ? JSON.stringify(value) : cgcode },
      extra: {
        query: context?.query,
        params: context?.params,
        url: context?.req?.url,
        headers: context?.req?.headers,
      }
    });
    return null;
  }
  return cgcode;
}
